/**
 * LeakTraceResolver — /leak/:token
 *
 * Landing page for QR codes embedded in watermarked PDFs / images.
 *
 * Two audiences share this route:
 *   1. **Public visitors** who scan a QR from a leaked printed page —
 *      they see a masked view proving the copy is licensed and traceable,
 *      plus a mailto link to legal@ for reporting.
 *   2. **Admins** who follow the same link while signed in with an admin
 *      account — they also see the full identity (email, IP, timestamp,
 *      user-agent, source asset URL) via `/api/admin/leak/{token}`.
 */
import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ShieldAlert, Mail, ArrowLeft, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const LeakTraceResolver = () => {
  const { token } = useParams();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin' || user?.role === 'moderator';

  const [publicView, setPublicView] = useState(null);
  const [adminView, setAdminView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Public endpoint is always safe (unauthenticated OK)
        const authHeader = window.localStorage.getItem('token');
        const opts = authHeader ? { headers: { Authorization: `Bearer ${authHeader}` } } : {};
        const { data } = await axios.get(`${API_BASE}/api/leak/${token}`, opts);
        if (cancelled) return;
        setPublicView(data);
        // Admins additionally get the full record
        if (isAdmin && authHeader && data.found) {
          try {
            const { data: adminData } = await axios.get(
              `${API_BASE}/api/admin/leak/${token}`,
              { headers: { Authorization: `Bearer ${authHeader}` } },
            );
            if (!cancelled) setAdminView(adminData);
          } catch (_) { /* admin-only fetch may fail for moderators; ignore */ }
        }
      } catch (_) {
        if (!cancelled) setPublicView({ found: false, token });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, isAdmin]);

  const copyToken = () => {
    navigator.clipboard?.writeText(token || '').then(() => {
      setCopied(true);
      toast.success('Token copied.');
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 md:px-6 py-10" data-testid="leak-trace-page">
        <Link to="/" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="w-3.5 h-3.5" /> Freeze Dry Academy
        </Link>

        <div className="flex items-start gap-3 mb-4">
          <div className="h-11 w-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0">
            <ShieldAlert className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Copyright · traceable copy</div>
            <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground leading-tight">
              Licensed course material
            </h1>
            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
              Trace token
              <code className="px-1.5 py-0.5 rounded bg-muted text-foreground text-[11px]" data-testid="trace-token">{token}</code>
              <button
                onClick={copyToken}
                className="hover:text-foreground"
                aria-label="Copy trace token"
                data-testid="copy-token-btn"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </p>
          </div>
        </div>

        {loading && (
          <Card><CardContent className="p-6 text-sm text-muted-foreground">Looking up trace…</CardContent></Card>
        )}

        {!loading && publicView && !publicView.found && (
          <Card data-testid="trace-not-found">
            <CardContent className="p-6">
              <p className="text-sm text-foreground">We couldn&rsquo;t find a copy matching that token.</p>
              <p className="text-xs text-muted-foreground mt-2">
                Either the QR was mis-scanned or the trace has been purged.
                Please email <a className="underline font-bold" href="mailto:legal@freezedry.academy">legal@freezedry.academy</a> with a photo of the copy you scanned.
              </p>
            </CardContent>
          </Card>
        )}

        {!loading && publicView?.found && (
          <>
            <Card className="mb-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Badge className="bg-primary/10 text-primary uppercase tracking-widest text-[10px]" variant="outline">Verified</Badge>
                  This is a licensed copy
                </CardTitle>
                <CardDescription>
                  Every download from Freeze Dry Academy carries a unique trace so
                  we can identify unauthorized redistribution.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="text-muted-foreground">Issued to</div>
                  <div className="col-span-2 font-bold text-foreground" data-testid="masked-email">{publicView.issued_to}</div>
                  <div className="text-muted-foreground">Kind</div>
                  <div className="col-span-2 uppercase font-mono">{publicView.kind}</div>
                  <div className="text-muted-foreground">Issued at</div>
                  <div className="col-span-2 font-mono">{publicView.issued_at ? new Date(publicView.issued_at).toLocaleString() : '—'}</div>
                </div>
                <p className="text-xs text-foreground/85 leading-relaxed pt-2 border-t border-border">
                  {publicView.notice}
                </p>
                <div className="pt-2">
                  <Button asChild variant="outline" className="w-full" data-testid="report-legal-btn">
                    <a href={`mailto:legal@freezedry.academy?subject=Suspected%20redistribution%20-%20trace%20${encodeURIComponent(token || '')}`}>
                      <Mail className="w-4 h-4 mr-2" />
                      Report to legal@freezedry.academy
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Admin-only full identity panel */}
            {isAdmin && adminView && (
              <Card className="border-red-500/50 bg-red-50/40 dark:bg-red-950/20" data-testid="admin-full-record">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-300">
                    <ShieldAlert className="w-4 h-4" />
                    Admin identity resolution
                  </CardTitle>
                  <CardDescription>Visible only to admin/moderator accounts.</CardDescription>
                </CardHeader>
                <CardContent className="text-xs font-mono space-y-1.5">
                  <div><span className="text-muted-foreground">email:</span> <strong className="text-foreground" data-testid="admin-email">{adminView.email}</strong></div>
                  <div><span className="text-muted-foreground">user_id:</span> {adminView.user_id}</div>
                  <div><span className="text-muted-foreground">lesson_id:</span> {adminView.lesson_id || '—'}</div>
                  <div><span className="text-muted-foreground">source_url:</span> <span className="break-all">{adminView.source_url}</span></div>
                  <div><span className="text-muted-foreground">ip:</span> {adminView.ip || '—'}</div>
                  <div className="break-all"><span className="text-muted-foreground">user_agent:</span> {adminView.user_agent || '—'}</div>
                  <div><span className="text-muted-foreground">created_at:</span> {adminView.created_at}</div>
                  <div className="pt-2 border-t border-red-500/30">
                    <Link
                      to={`/admin/users/${adminView.user_id}`}
                      className="text-xs font-bold text-red-700 dark:text-red-300 underline"
                      data-testid="jump-to-user"
                    >
                      Jump to user in the admin console →
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LeakTraceResolver;
